class A11yMenu {
    menubar = null;

    menuItems = null;

    menuItemsAll = null;

    submenu$ = null;

    constructor(navigation) {
        this.menubar = navigation;
        this.initNavigation();
    }

    set currentSubmenu(node) {
        this.submenu$ = node;
    }

    get currentSubmenu() {
        return this.submenu$;
    }

    initNavigation() {
        this.menuItems = this.menubar.querySelectorAll('[role="menuitem"][aria-haspopup="true"]');
        this.menuItemsAll = this.menubar.querySelectorAll('[role="menuitem"]');

        this.registerEvents();
    }

    registerEvents() {
        this.menuItemsAll.forEach((item) => {
            // Keyboard Navigation
            item.addEventListener('keydown', this.handleMenuKeydown.bind(this, item), true);

            // Close submenus on blur
            item.addEventListener('blur', () => {
                // Optional: Mit setTimeout delay verhindern, dass das Schließen zu früh passiert
                setTimeout(() => {
                    if (document.activeElement.closest('.nav-submenu-container') === null) {
                        this.closeAllSubmenus();
                    }
                }, 100);
            });
        });
    }

    static getSubmenuFromDOM(item) {
        return item.parentElement.querySelector('.nav-submenu-container');
    }

    static getTopLevelItem(element) {
        if (!element.hasAttribute('aria-haspopup')) {
            const parentMenu = element.closest('.nav-li');
            return parentMenu.querySelector('[aria-haspopup]');
        }

        return element;
    }

    static focusNext(items, currentIndex) {
        const next = (currentIndex + 1) % items.length;
        items[next].focus();
    }

    static focusPrev(items, currentIndex) {
        const prev = (currentIndex - 1 + items.length) % items.length;
        items[prev].focus();
    }

    static focusHome(items) {
        items[0]?.focus();
    }

    static focusEnd(items) {
        items[items.length - 1]?.focus();
    }

    handleMenuKeydown(item, event) {
        const {key, shiftKey} = event;
        const parentMenu = item.closest('[role="menubar"], [role="menu"]');
        const siblings = Array.from(parentMenu.querySelectorAll('[role="menuitem"]'));

        const menubarTopLevelList = Array.from(this.menuItemsAll).filter((sibling) => {
            return sibling.classList.contains('nav-link');
        });

        const currentItemIndex = menubarTopLevelList.indexOf(A11yMenu.getTopLevelItem(item));

        switch (key) {
            case 'ArrowRight':
                event.preventDefault();

                if (parentMenu.getAttribute('role') !== 'menubar') {
                    this.closeAllSubmenus();
                }

                A11yMenu.focusNext(menubarTopLevelList, currentItemIndex);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                if (parentMenu.getAttribute('role') !== 'menubar') {
                    this.closeAllSubmenus();
                }

                A11yMenu.focusPrev(menubarTopLevelList, currentItemIndex);
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (item.hasAttribute('aria-haspopup')) {
                    this.openSubmenu(item);
                    this.focusFirstSubitem(item);
                } else {
                    A11yMenu.focusNext(siblings, siblings.indexOf(item));
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (item.hasAttribute('aria-haspopup')) {
                    this.openSubmenu(item);
                    this.focusLastSubitem(item);
                } else {
                    A11yMenu.focusPrev(siblings, siblings.indexOf(item));
                }
                break;
            case 'Home':
                event.preventDefault();
                A11yMenu.focusHome(siblings);
                break;
            case 'End':
                event.preventDefault();
                A11yMenu.focusEnd(siblings);
                break;
            case 'Escape':
                event.preventDefault();
                // eslint-disable-next-line no-case-declarations
                const wrapper = item.closest('.nav-submenu-container');
                // eslint-disable-next-line no-case-declarations
                const parentItem = wrapper?.parentElement?.querySelector('[role="menuitem"]');
                if (parentItem) {
                    this.closeAllSubmenus();
                    parentItem.focus();
                } else {
                    this.closeAllSubmenus();
                    item.focus();
                }
                break;
            case 'Enter':
            case ' ':
                if (item.hasAttribute('aria-haspopup')) {
                    event.preventDefault();
                    this.toggleSubmenu(item);
                }
                break;
            case 'Tab':
                if (item.hasAttribute('aria-haspopup') && !shiftKey) {
                    this.openSubmenu(item);
                }
                break;
            default:
                break;
        }
    }

    closeAllSubmenus() {
        this.menuItems.forEach((item) => {
            this.currentSubmenu = A11yMenu.getSubmenuFromDOM(item);
            if (this.currentSubmenu) {
                this.currentSubmenu.removeAttribute('style');
                item.setAttribute('aria-expanded', 'false');
            }
        });
    }

    openSubmenu(item) {
        this.currentSubmenu = A11yMenu.getSubmenuFromDOM(item);
        if (this.currentSubmenu) {
            this.currentSubmenu.style.height = '140px';
            item.setAttribute('aria-expanded', 'true');
        }
    }

    toggleSubmenu(item) {
        const expanded = item.getAttribute('aria-expanded') === 'true';
        if (expanded) {
            this.closeAllSubmenus();
        } else {
            this.closeAllSubmenus();
            this.openSubmenu(item);
            this.focusFirstSubitem(item);
        }
    }

    focusFirstSubitem(item) {
        this.currentSubmenu = A11yMenu.getSubmenuFromDOM(item);
        if (this.currentSubmenu) {
            const first = this.currentSubmenu.querySelector('[role="menuitem"]');
            first?.focus();
        }
    }

    focusLastSubitem(item) {
        this.currentSubmenu = A11yMenu.getSubmenuFromDOM(item);
        const last = this.currentSubmenu?.querySelector('li:last-child [role="menuitem"]');
        last?.focus();
    }
}

export default function initA11yNavigation() {
    const navigation = document.querySelector('.js-a11y-navigation');
    return new A11yMenu(navigation);
}
